/* eslint-disable react/prop-types */

import React from "react";
import { Button, ModalBody, ModalFooter } from "reactstrap";
import { Col, Modal, Row, ModalHeader } from "reactstrap";
import { formatDateDDMMM, formatDateYYYYMMDD } from "../../../helpers/utils";
import { useEffect, useState } from "react";
import { Calendar as CalendarIcon, FileText } from 'react-feather';
import Calendar from 'react-calendar';
import { NutritionEntity, SprayCampaign, SprayExecutedProduct, SprayProduct } from "../../../types";
import { DateAttributeParams } from "../../../components/EditDateAttribute";
import { EditSelectAttribute, SelectAttributeParams } from "../../../components/EditSelectAttribute";
import { EditQuantity, QuantityProps } from "../../../components/EditQuantity";
import { useDrivers, usePreparers, useProducts, useSprayers, useTractors, useUnits } from "../../../helpers/hooks";
import { Section } from "../../../components/Section";
import { ProductCard } from "../../../components/ProductCard";
import { useMutationSaveMultipleSprayExecuted } from "../../../services/stores/queries";
import { toast } from "react-hot-toast";

// Type for react-calendar onChange value
type CalendarValue = Date | Date[] | null | [Date | null, Date | null];

interface EditSprayTankHeaderProps {
  sprayPlanned: SprayCampaign;
  showModal: boolean;
  setShowModal: (showConfirmModal: boolean) => void;
  completionDate: Date;
  setCompletionDate: (completionDate: Date) => void;
  products: SprayProduct[];
}

interface SprayExecutedFormState {
  sprayTankCount: number;
  treatmentID: string;
  isDirty: boolean;
  description: string;
  startDate: string;
  endDate: string;
  volumeL: number;
  tractorID: string;
  sprayerID: string;
  driverID: string;
  preparerID: string;
  products: SprayExecutedProduct[];
}

const DEFAULT_VALUES = {
  VOLUME_L: 2000,
} as const;

const InfoRow = ({ icon: Icon, label, value }: { icon?: any, label?: string | null, value?: string | number | null }) => {
 
  if (!label) return null;
  if (!value) return null;

  return (
    <>
    <div className="d-flex align-items-center mb-2">
      <div className="d-flex align-items-center" style={{ fontWeight: 'bold', color: 'blue' }}>
        <Icon size={18} className="me-2" />
        <span className="fw-bold me-2">{label}:</span>
      </div>

      </div>
      <div className="d-flex align-items-center mb-2">
        <div className="d-flex align-items-center" style={{ marginLeft : '40px' }}>
          <span>{value}</span>
        </div>

    </div>
    </>
  );
};

export const EditSprayTankHeader = ({
  sprayPlanned,
  products,
  showModal,
  setShowModal
}: EditSprayTankHeaderProps) => {

    const [showConfirmCalendar, setShowConfirmCalendar] = useState(false);
    const [showConfirmSave, setShowConfirmSave] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date(sprayPlanned.plannedStartDate));
    const [pendingSelectedDate, setPendingSelectedDate] = useState<Date>(new Date(sprayPlanned.plannedStartDate));
    const saveMultipleSprayExecuted = useMutationSaveMultipleSprayExecuted();

    const [formState, setFormState] = useState<SprayExecutedFormState>({
      isDirty: false,
      sprayTankCount: Math.ceil(sprayPlanned.plannedTotalVolumeL / sprayPlanned.plannedSprayTankVolumeL),
      treatmentID: sprayPlanned.id,
      description: sprayPlanned.description,
      volumeL: sprayPlanned.plannedSprayTankVolumeL || DEFAULT_VALUES.VOLUME_L,
      startDate: sprayPlanned.plannedStartDate,
      endDate: sprayPlanned.plannedStartDate,
      tractorID: '',
      sprayerID: '',
      driverID: '',
      preparerID: '',
      products: []  // Start with empty array
    });

    // Add this effect to set products when they're available
    useEffect(() => {
      if (products && products.length > 0) {
        setFormState(prev => ({
          ...prev,
          products: products.map(p => ({
            id: '',
            productID: p.productID,
            quantity: p.plannedProductQuantity,
            unitID: p.plannedProductQuantityUnitID,
            plannedProductQuantity: p.plannedProductQuantity,
            plannedProductQuantityUnitID: p.plannedProductQuantityUnitID,
            isDirty: false,
          }))
        }));
      }
    }, [products]);  // Only run when products changes

    const { drivers, toDriverName } = useDrivers();
    const { tractors, toTractorName } = useTractors();
    const { sprayers, toSprayerName } = useSprayers ();
    const { preparers, toPreparerName } = usePreparers();
    const { toProductName } = useProducts();
    const {toUnitName } = useUnits();

    const updateFormState = (field: keyof typeof formState, value: any) => {
      setFormState(prev => ({ ...prev, [field]: value, isDirty: true }));
    };

    const createComponentProps = () => ({

      startDate : {
          name: 'startDate',
          value: formState.startDate,
          label: 'Date',
          required: true,
          onChangeCallback: (value?: string) => 
              updateFormState('startDate', value),
      } as DateAttributeParams,

      sprayTankCount: {
        name: 'sprayTankCount',
        value: formState.sprayTankCount,
        label: 'Nombre de citernes',
        required: true,
        onChangeCallback: (value?: number) => 
            updateFormState('sprayTankCount', value),
    } as QuantityProps,

      volumeL: {
          name: 'volumeL',
          value: formState.volumeL,
          label: 'Cuve (L)',
          required: true,
          onChangeCallback: (value?: number) => 
              updateFormState('volumeL', value),
      } as QuantityProps,


      tractorID: {
          name: 'tractorID',
          value: formState.tractorID,
          label: 'Tracteur',
          options: tractors  || [],
          required: true,
          valueField: 'tractorID',
          labelField: 'tractorShortCode',
          onChangeCallback: (value?: string) => 
              updateFormState('tractorID', value),
      } as SelectAttributeParams,

      sprayerID: {
          name: 'sprayerID',
          value: formState.sprayerID,
          label: 'Pulvérisateur',
          options: sprayers  || [],
          required: true,
          valueField: 'sprayerID',
          labelField: 'sprayerName',
          onChangeCallback: (value?: string) => 
              updateFormState('sprayerID', value),
      } as SelectAttributeParams,

      driverID: {
          name: 'driverID',
          value: formState.driverID,
          label: 'Conducteur',
          options: drivers  || [],
          required: true,
          valueField: 'userID',
          labelField: 'fullName',
          onChangeCallback: (value?: string) => 
              updateFormState('driverID', value),
      } as SelectAttributeParams,

      preparerID: {
          name: 'preparerID',
          value: formState.preparerID,
          label: 'Préparateur',
          options: preparers || [],
          required: true,
          valueField: 'userID',
          labelField: 'fullName',
          onChangeCallback: (value?: string) => 
              updateFormState('preparerID', value),
      } as SelectAttributeParams,
  });

  const props = createComponentProps();

  
    const toggleConfirmCalendar = () => {
        setPendingSelectedDate(selectedDate);
        setShowConfirmCalendar(!showConfirmCalendar);
    }

    const handleApplicationDateChange = (value: CalendarValue) => {
        setPendingSelectedDate(value as Date);
      };

      const onConfirmCalendarDate = () => {
        setSelectedDate(pendingSelectedDate);
        const startDate = (formatDateYYYYMMDD(pendingSelectedDate) || '');
        setFormState(prev => ({ ...prev, startDate: startDate, endDate: startDate, isDirty: true }));
        setShowConfirmCalendar(false);
      };

      const onCloseCalendar = () => {
        setShowConfirmCalendar(false);
      };

      const onCancelEditSprayTankHeader = () => {
        setShowModal(false);
      }

      const onSaveEditSprayTankHeader = () => {

        saveMultipleSprayExecuted.mutate({
          spray: {
            id: '',
            treatmentID: sprayPlanned.id,
            sprayTankIndex: 0,
            blockID: sprayPlanned.blockID,
            tractorID: formState.tractorID,  
            sprayerID: formState.sprayerID,
            driverID: formState.driverID,
            preparerID: formState.preparerID,
            volumeL: formState.volumeL,
            startDate: (formatDateYYYYMMDD(formState.startDate) || '') + ' 00:00:00',
            endDate: (formatDateYYYYMMDD(formState.startDate) || '') + ' 00:00:00',
            products: formState.products,
            isDirty: true,
            farmID: sprayPlanned.farmID,
          },
          sprayTankCount: formState.sprayTankCount
        },{
          onSuccess: () => {
            toast.success("Les citernes ont été enregistrées avec succès");
            setShowModal(false);
            setShowConfirmSave(false);
          },
          onError: (error) => {
            toast.error(error.message);
          }
        });
      }

      const onCancelConfirmAndSave = () => {
        setShowConfirmSave(false);
      }
      
      const onProductEdit = (product: SprayExecutedProduct | SprayProduct | NutritionEntity, editAmount: number | undefined) => {

        const productEdited = formState.products.find(p => p.productID === product.productID);
        if (productEdited) {
          productEdited.quantity = editAmount || 0;
        }
      }
      
  const promptForData = () => {
    return (
      <Modal isOpen={showModal} toggle={() => onCancelEditSprayTankHeader()}>
            
            <ModalHeader toggle={() => onCancelEditSprayTankHeader()}>
              Données générales
            </ModalHeader>

            <ModalBody>

              <Section title="Date de réalisation">
              <div className="mb-4">
                <Row   onClick={toggleConfirmCalendar}                >
                  <Col xs={8}>
                      <h3 
                      className="text-primary clickable-date"
                      style={{ cursor: 'pointer', fontSize: '1.2rem', fontWeight: 'bold', marginLeft: '10px' }}
                    >
                      {formatDateYYYYMMDD(formState.startDate)}
                    </h3>
                  </Col>
                   <Col xs={4}>
                    <CalendarIcon  />
                  </Col>
                </Row>
              </div>


              {showConfirmCalendar && (
                  <div className="calendar-wrapper">
                    <Calendar
                      onChange={handleApplicationDateChange}
                      value={pendingSelectedDate}
                      className="rounded shadow-sm"
                      formatShortWeekday={(locale, date) => {
                        const day = date.getDay();
                        const weekDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
                        return weekDays[day];
                      }}
                    />
                    <div className="calendar-actions">
                      <Button color="secondary" outline onClick={onCloseCalendar} className="calendar-action-button">
                        Fermer
                      </Button>
                      <Button color="primary" onClick={onConfirmCalendarDate} className="calendar-action-button">
                        OK
                      </Button>
                    </div>
                  </div>
                )} 

              </Section>
              <Section title="Citernes">
                <Row className="form-row">
                    <Col className="form-col" xs = {12} md = {5}>
                        <EditQuantity {...props.sprayTankCount} />
                    </Col>
                    <Col className="form-col" xs ={12} md = {5}>
                        <EditSelectAttribute {...props.sprayerID} />
                    </Col>
                    <Col className="form-col" xs = {12} md = {5}>
                        <EditQuantity {...props.volumeL} />
                    </Col>
                  </Row>
              </Section>
               <Section title="Données générales">
                <Row className="form-row">

                    <Col className="form-col" xs ={12} md = {5}>
                        <EditSelectAttribute {...props.tractorID} />
                    </Col>
                    </Row>
                <Row className="form-row">
                    <Col className="form-col" xs ={12} md = {5}>
                        <EditSelectAttribute {...props.driverID} />
                    </Col>
                    <Col className="form-col" xs ={12} md = {5}>
                        <EditSelectAttribute {...props.preparerID} />
                    </Col>
                </Row>
                </Section>

                <Section title="Produits" >
                <div className="product-cards">
                {formState.products?.map((product: SprayExecutedProduct, indexProduct: number) => (
                      <div key={ indexProduct}>
                        <ProductCard
                          key={ indexProduct} 
                          product={product} 
                          canEdit={true}
                          onEdit={onProductEdit}
                        />
                      </div>
                  ))}
                </div>
                </Section>

            </ModalBody>
            
            <ModalFooter>
              <Button color="secondary" onClick={onCancelEditSprayTankHeader}>
                Annuler
              </Button>
              <Button color="success" onClick={() => setShowConfirmSave(true)}>
                Envoyer
              </Button>
            </ModalFooter>
          </Modal>


  )};
  const comfirmAndSave = () => {
    return (
      <Modal isOpen={showConfirmSave} toggle={() => onCancelConfirmAndSave()}>
            
            <ModalHeader toggle={() => onCancelConfirmAndSave()}>
              Confirmation finale
            </ModalHeader>

            <ModalBody>

              <Section title="Date de réalisation">
              <InfoRow
                        icon={CalendarIcon}
                        label= {'Date'}
                        value={formatDateDDMMM (new Date(formState.startDate))}
                      />
              </Section>
              <Section title="Matériel">
                      <InfoRow
                        icon={FileText}
                        label= {props.sprayTankCount?.label || 'Nombre de citernes'}
                        value={props.sprayTankCount?.value}
                      />
                      <InfoRow
                        icon={FileText}
                        label= {props.sprayerID?.label || 'Pulvérisateur'}
                        value={toSprayerName(props.sprayerID?.value)  } 
                      />
                      <InfoRow
                        icon={FileText}
                        label= {props.volumeL?.label || 'Cuve (L)'}
                        value={props.volumeL?.value + ' L' }
                      />
                      <InfoRow
                        icon={FileText}
                        label= {props.tractorID?.label || 'Tracteur'}
                        value={toTractorName(props.tractorID?.value)  } 
                      />                    
              </Section>
               <Section title="Personnel">
                <InfoRow
                  icon={FileText}
                  label= {props.driverID?.label || 'Conducteur'}
                  value={toDriverName(props.driverID?.value)  } 
                />
                <InfoRow
                  icon={FileText}
                  label= {props.preparerID?.label || 'Préparateur'}
                  value={toPreparerName(props.preparerID?.value)  } 
                />

              </Section>

                <Section title="Produits" >
                <div className="product-cards">
                {formState.products?.map((product: SprayExecutedProduct, indexProduct: number) => (
                  <Row key={indexProduct} style={{ borderBottom: '1px solid #ccc', paddingBottom: '10px' }}>
                      <Col xs={5} md={5}>
                      {toProductName(product.productID)}
                    </Col>
                    <Col xs={1} md={1}>
                      {toUnitName(product.unitID)}
                    </Col>                  
                    <Col xs={3} md={3}>
                      {product.plannedProductQuantity}
                    </Col> 
                    <Col xs={3} md={1}>
                      {product.quantity}
                    </Col>
                  </Row>
                  ))}
                </div>
                </Section>

                <Section title="Quantités totales" >
                <div className="product-cards">
                {formState.products?.map((product: SprayExecutedProduct, indexProduct: number) => (
                  <Row key={indexProduct} style={{ borderBottom: '1px solid #ccc', paddingBottom: '10px' }}>
                      <Col xs={7} md={3}>
                      {toProductName(product.productID)}
                    </Col>
                    <Col xs={5} md={3}>
                      {formState.sprayTankCount * product.quantity}  {toUnitName(product.unitID)}
                    </Col>                  
                  </Row>
                  ))}
                </div>
                </Section>


            </ModalBody>
            
            <ModalFooter>
              <Button color="secondary" onClick={onCancelConfirmAndSave}>
                Annuler
              </Button>
              <Button color="success" onClick={onSaveEditSprayTankHeader}>
                Envoyer
              </Button>
            </ModalFooter>
          </Modal>
    );
  };

  return (
    <div>
      {!showConfirmSave && promptForData()}
      {showConfirmSave && comfirmAndSave()}
    </div>
  );
};


/*

              <div className="applications-list">
                {selectedApplications.map((nutrition, index) => (
                  <div key={index} className="mb-3 p-3 border rounded">
                    <h6>{toProductName(nutrition.productID)}</h6>
                    <div className="d-flex justify-content-between">
                      <span>Programmé: {nutrition.quantity} {toUnitName(nutrition.unitID)}</span>
                      <span>Réalisé: {nutrition.actualQuantity || '0'} {toUnitName(nutrition.unitID)}</span>
                    </div>
                  </div>
                ))}
              </div>

              */